import {
  Button,
  Icon,
  IconButton,
  Tooltip,
  Typography,
  VerticalDivider
} from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import { maxBy } from 'lodash';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';

import { queryClient } from '@hero/clients';
import { DuplicateModal } from '@hero/components/modals';
import {
  CONTACT_TYPE,
  dateLabelFormat,
  FLOW_CODES,
  HOME_RECOGNITIONS_TABLE_LIMIT,
  PAYMENT_METHOD_TYPES,
  QUERY_KEYS,
  RECOGNITION_CREATION_STEPS,
  RECOGNITION_STATUSES,
  SEND_GIFT_FLOW_STEPS,
  SEND_GIFT_ROUTE_PATHS
} from '@hero/constants';
import {
  useDeleteModalTemplate,
  useRecognitionCreateModal,
  useSendRemindersNotification
} from '@hero/hooks';
import { OptionsDropdown, OrderStatusBadge, Table } from '@hero/molecules';
import { ROUTE_PATHS } from '@hero/routes';
import { recognitionServices } from '@hero/services';
import {
  useDeleteRecognition,
  useDuplicateRecognition,
  useGetRecognitions,
  useSendRemindersRecognition,
  useStartRecognition,
  useUpdateRecognition
} from '@hero/services/order-service/recognition/hooks';
import { useExpireRecognition } from '@hero/services/order-service/recognition/hooks/mutations/useExpireRecognition';
import { useNewRecognitionStore, useNotification } from '@hero/store/hooks';
import { formatCurrency, formatDate } from '@hero/utils/helper';

import { ExpandedRecognitionRow } from './ExpandedRecognitionRow';
import * as S from './style';
import { reactivateFormValidationSchema } from './validationSchemas';

function getReactivateForm({ isCCPayment }) {
  const recognitionReactivateForm = [
    {
      name: 'expirationDate',
      placeholder: <Trans i18nKey="redemptionDeadline" />,
      type: 'date',
      excludePast: true,
      showTimeSelect: true,
      ...(isCCPayment && { maxDate: dayjs().add(90, 'days').toDate() })
    }
  ];
  return { recognitionReactivateForm };
}

function RecentRecognitionsExpandableTable() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const { showRecognitionCreateModal } = useRecognitionCreateModal();
  const draftStatuses = [
    RECOGNITION_STATUSES.draft,
    RECOGNITION_STATUSES.pending_approval,
    RECOGNITION_STATUSES.approved
  ];

  const { mutateAsync: updateRecognition } = useUpdateRecognition();
  const { mutateAsync: duplicateRecognition } = useDuplicateRecognition();
  const { mutateAsync: mutateStartRecognition } = useStartRecognition();
  const { mutateAsync: expireRecognition } = useExpireRecognition();

  const [duplicateModalData, setDuplicateModalData] = useState({});

  const { showDeleteModal } = useDeleteModalTemplate({
    title: t('areYouSureYouWantDelete', { type: t('oneTimeGift').toLowerCase() }),
    message: t('deleteRecognitionModalDesc'),
    confirmText: 'DELETE GIFT',
    maxLength: 18
  });
  const { mutateAsync: deleteRecognition } = useDeleteRecognition();

  const isRecognitionStatusDraft = original => draftStatuses.includes(original?.status?.current);

  const [globalFilters, setGlobalFilters] = useState({});
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: HOME_RECOGNITIONS_TABLE_LIMIT
  });

  const {
    data: myRecognitions,
    isLoading: isLoadingRecognitions,
    refetch
  } = useGetRecognitions({
    filters: {
      searchTerm: globalFilters.searchTerm
    },
    pagination: {
      page: pageIndex + 1,
      limit: pageSize
    }
  });

  const {
    updateProducts,
    updateContacts,
    updateExperienceSettings,
    updateMeta,
    updateCode,
    updateName,
    updateSettings
  } = useNewRecognitionStore();

  const { send: sendRemindersRecognition } = useSendRemindersNotification({
    useSendReminder: useSendRemindersRecognition
  });

  const onNavigateToDetailPage = async row => {
    if (isRecognitionStatusDraft(row?.original)) {
      if (row.original?.meta?.creationStep !== RECOGNITION_CREATION_STEPS.newRecognitionReviewPay) {
        updateContacts({
          ...row.original.contacts,
          maxCount: row.original?.contacts?.maxCount?.toString() || '0'
        });
        updateName(row.original.name);
        updateCode(row.original.code);
        updateExperienceSettings(row.original.experienceSettings || {});
        updateMeta(row.original.meta);
        updateSettings(row.original.settings);
        updateProducts(row.original?.products);

        if (
          Object.values(SEND_GIFT_FLOW_STEPS).find(
            step => step === row.original?.meta?.creationStep
          )
        ) {
          navigate(
            ROUTE_PATHS[SEND_GIFT_ROUTE_PATHS[row.original?.meta?.creationStep]](row.original.code)
          );
        } else navigate(ROUTE_PATHS[row.original?.meta?.creationStep]);
      } else {
        navigate(ROUTE_PATHS.NEW_RECOGNITION_REVIEW_PAY(row.original.code));
      }
    } else {
      navigate({
        pathname: ROUTE_PATHS.MY_RECOGNITION_DETAIL(row.original.code),
        search: `?${createSearchParams({
          status: row.original?.status?.current
        })}`
      });
    }
  };

  const onArchiveModal = row => {
    showNotification({
      popupStyle: {
        maxWidth: '500px'
      },
      formStyle: {
        padding: '0 50px'
      },
      title: t('archiveFeaturePromptTitle', { featureType: t('oneTimeGift').toLocaleLowerCase() }),
      message: t('areYouSureWantToArchiveDesc', {
        featureType: t('oneTimeGift').toLocaleLowerCase()
      }),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      primaryButtonProps: { color: 'error', children: t('archive') },
      secondaryButtonProps: { children: t('dontArchive') },
      onSubmit: async () => {
        await updateRecognition({
          params: { recognitionCode: row.original.code },
          body: {
            status: { current: RECOGNITION_STATUSES.archived }
          }
        });
        refetch();
      }
    });
  };

  const onExpireModal = row => {
    showNotification({
      popupStyle: {
        maxWidth: '500px'
      },
      title: t('areYouSure'),
      message:
        row.original?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard ? (
          <Trans
            i18nKey="expireRecognitionPromptMessageCC"
            values={{
              unOpenedCount: row.original.numberOfContacts - row?.original.contacts?.redeemCount
            }}
            components={{ b: <b /> }}
          />
        ) : (
          <Trans
            i18nKey="expireRecognitionPromptMessageHouseAccount"
            values={{
              unOpenedCount: row.original.numberOfContacts - row?.original.contacts?.redeemCount
            }}
            components={{ b: <b /> }}
          />
        ),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      secondaryButtonProps: { children: t('noKeepActive') },
      primaryButtonProps: { color: 'error', children: t('yesEndIt') },
      onSubmit: async () => {
        await expireRecognition({
          code: row.original.code
        });
        refetch();
      }
    });
  };

  const onReactivateModal = async row => {
    const spendingSummary = await queryClient.fetchQuery(
      [QUERY_KEYS.useGetRecognitionSpendingSummaryKey, row.original.code],
      () => recognitionServices.spendingSummary(row.original.code)
    );

    const isCCPayment = row.original?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard;
    const { recognitionReactivateForm } = getReactivateForm({ isCCPayment });

    showNotification({
      notification: {
        title: t('reactivateNotificationTitle'),
        message: (
          <Trans
            i18nKey="reactivateNotificationMessage"
            values={{
              totalAmount: formatCurrency(
                spendingSummary.estimatedCharges.total - spendingSummary.spendingSummary.total
              )
            }}
            components={{ b: <b /> }}
          />
        ),
        status: 'info'
      },
      notificationImage: <Icon name="calendar" size="xxl" />,
      header: t('reactivateModalTitleDate'),
      message: t('reactivateModalDescDate'),
      formData: recognitionReactivateForm,
      formValidationSchema: reactivateFormValidationSchema,
      primaryButtonProps: {
        endIcon: 'next',
        children: t('reactivate')
      },
      secondaryButtonProps: { children: t('cancel') },
      onSubmit: async ({ formValues }) => {
        await updateRecognition({
          params: { recognitionCode: row.original.code },
          body: {
            settings: {
              expiresAt: formValues.expirationDate
            },
            status: { current: RECOGNITION_STATUSES.live }
          }
        });

        refetch();
      }
    });
  };

  const onDeleteModal = async row => {
    showDeleteModal({
      onSubmit: async () => {
        await deleteRecognition(row.original.code);
      }
    });
  };

  const columns = React.useMemo(
    () => [
      {
        id: 'expand',
        header: '',
        cell: ({ cell: { row } }) => {
          if (
            row.original.status.current !== RECOGNITION_STATUSES.live &&
            row.original.status.current !== RECOGNITION_STATUSES.expired
          ) {
            return (
              <Tooltip content={t('noDataToShow')}>
                <IconButton disabled variant="link" size="sm">
                  <Icon color="var(--color-fg-weak)" name="right-chevron" />
                </IconButton>
              </Tooltip>
            );
          }
          return (
            <IconButton
              onClick={() => {
                row.toggleExpanded();
              }}
              variant="link"
              size="sm"
              style={{
                cursor: 'pointer'
              }}
            >
              <Icon
                color="var(--color-fg-weak)"
                name={row.getIsExpanded() ? 'down-chevron' : 'right-chevron'}
              />
            </IconButton>
          );
        },
        size: 10
      },
      {
        id: 'name',
        header: t('name'),
        accessorKey: 'name',
        size: 150,
        cell: ({ cell: { row } }) => {
          return (
            <Typography
              variant="body2"
              as="p"
              lineClamp={1}
              onClick={() => onNavigateToDetailPage(row)}
              underline
              style={{ cursor: 'pointer' }}
              title={row.original.name}
            >
              {row.original.name}
            </Typography>
          );
        }
      },
      {
        id: 'contact',
        header: t('recipients'),
        size: 20,
        accessorKey: 'numberOfContacts',
        sortType: 'number'
      },
      {
        id: 'expires-date',
        header: t('redemptionDeadline'),
        cell: ({ cell: { row } }) => {
          return formatDate(row.original?.settings?.expiresAt, {
            format: dateLabelFormat,
            includeTime: false
          });
        },
        screen: ['tablet', 'desktop']
      },
      {
        header: t('status'),
        accessor: 'status',
        cell: ({ cell: { row } }) => {
          const data = row?.original;
          const currentStatus = data?.status.current;
          const isReadyToSend =
            currentStatus === RECOGNITION_STATUSES.draft &&
            data?.meta?.creationStep === RECOGNITION_CREATION_STEPS.newRecognitionReviewPay;

          return (
            <OrderStatusBadge
              status={isReadyToSend ? RECOGNITION_STATUSES.readyToSend : currentStatus}
            />
          );
        }
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: ({ row }) => {
          const isDraft =
            row.original.status.current === RECOGNITION_STATUSES.draft &&
            row.original?.meta?.creationStep !== RECOGNITION_CREATION_STEPS.newRecognitionReviewPay;
          const isReadyToSend =
            row.original.status.current === RECOGNITION_STATUSES.draft &&
            row.original?.meta?.creationStep === RECOGNITION_CREATION_STEPS.newRecognitionReviewPay;
          const isLive = row.original.status.current === RECOGNITION_STATUSES.live;
          const isExpired = row.original.status.current === RECOGNITION_STATUSES.expired;
          const isProcessing = row.original.status.current === RECOGNITION_STATUSES.starting;
          const isProcessingError =
            row.original.status.current === RECOGNITION_STATUSES.error_on_start;

          const isContact = row.original?.contacts.type !== CONTACT_TYPE.number;

          const skus = row.original.products.reduce((result, product) => {
            return [...result, ...product.skus];
          }, []);

          let options = [];

          if (isLive && isContact) {
            options.push({
              label: t('remindRecipients'),
              onClick: () => sendRemindersRecognition(row.original.code)
            });
          }

          options = options.concat([
            {
              label: t(isDraft ? 'keepEditing' : 'seeDetails'),
              onClick: () => onNavigateToDetailPage(row)
            },
            {
              label: t('duplicate'),
              onClick: () => {
                setDuplicateModalData({
                  isOpen: true,
                  setIsOpen: () => {
                    setDuplicateModalData({
                      isOpen: false
                    });
                  },
                  code: row.original.code,
                  duplicateText: row.original.name,
                  recipientCount: row.original.numberOfContacts,
                  giftCount: row.original.products.length,
                  contactType: row.original.contacts.type,
                  giftMessage: row.original.experienceSettings?.messageParams?.message,
                  giftPrice: maxBy(skus, 'price')?.price,
                  meta: row.original.meta,
                  flowCode: row.original.flowCode
                });
              }
            }
          ]);

          if (isLive) {
            options.push({
              label: t('endGift'),
              color: 'error',
              style: { color: 'var(--color-error-default)' },
              onClick: () => onExpireModal(row)
            });
          }

          if (isExpired) {
            options.push({
              label: t('reactivate'),
              onClick: () => {
                onReactivateModal(row);
              }
            });
            options.push({
              label: t('archive'),
              onClick: () => onArchiveModal(row)
            });
          }

          if (isDraft || isReadyToSend) {
            options.push({
              label: t('delete'),
              color: 'error',
              style: { color: 'var(--color-error-default)' },
              onClick: () => {
                onDeleteModal(row);
              }
            });
          }

          if (isProcessingError) {
            options.splice(1, 1);
          }
          if (isProcessing) {
            options.splice(1, 1);
          }

          if (isProcessingError) {
            options.push({
              label: t('resubmit'),
              onClick: () => mutateStartRecognition(row.original.code)
            });
          }

          return (
            <OptionsDropdown
              popperOptions={{
                placement: 'left',
                modifiers: [
                  {
                    name: 'offset',
                    enabled: true,
                    options: {
                      offset: [50, 0]
                    }
                  }
                ]
              }}
              options={options}
            >
              <Icon name="3dot" px={18} />
            </OptionsDropdown>
          );
        }
      }
    ],
    []
  );

  const dataWithSubRows = useMemo(() => {
    if (!myRecognitions?.docs || myRecognitions?.docs?.length === 0) return [];

    return myRecognitions?.docs?.map(doc => ({
      ...doc
    }));
  }, [myRecognitions?.docs]);

  return (
    <S.Recognition>
      <S.Header>
        <Icon name="gift-box" size="lg" />
        <Typography variant="body1Bold">{t('recentRecognitions')}</Typography>
        <VerticalDivider $height="16px" $margin="0 12px" />
        <Button variant="link" onClick={() => navigate(ROUTE_PATHS.MY_RECOGNITIONS)}>
          {t('seeAll')}
        </Button>
      </S.Header>
      <Table
        columns={columns}
        data={dataWithSubRows || []}
        globalFilters={globalFilters}
        pagination={{
          pageIndex,
          pageSize,
          total: myRecognitions?.pagination?.total
        }}
        manualPagination
        loading={isLoadingRecognitions}
        setGlobalFilters={setGlobalFilters}
        setPagination={setPagination}
        pageCount={myRecognitions?.pagination?.pages}
        emptyTableContent={{
          createNew: showRecognitionCreateModal,
          feature: 'recognition',
          icon: 'gift-box'
        }}
        renderSubRow={row => {
          return (
            <ExpandedRecognitionRow onNavigateToDetailPage={onNavigateToDetailPage} row={row} />
          );
        }}
      />
      {duplicateModalData?.isOpen && (
        <DuplicateModal
          isOpen={duplicateModalData.isOpen}
          onOpen={duplicateModalData.setIsOpen}
          onSubmit={async formValues => {
            const response = await duplicateRecognition({
              code: duplicateModalData.code,
              body: formValues
            });

            if (duplicateModalData.flowCode === FLOW_CODES.standard) {
              navigate(ROUTE_PATHS[duplicateModalData.meta.creationStep](response.code));
            }

            if (duplicateModalData.flowCode === FLOW_CODES.simplified) {
              navigate(
                ROUTE_PATHS[SEND_GIFT_ROUTE_PATHS[duplicateModalData.meta.creationStep]](
                  response.code
                )
              );
            }
          }}
          featureType="recognition"
          duplicateText={duplicateModalData.duplicateText}
          recipientCount={duplicateModalData.recipientCount}
          giftCount={duplicateModalData.giftCount}
          contactType={duplicateModalData.contactType}
          giftMessage={duplicateModalData.giftMessage}
          giftPrice={duplicateModalData.giftPrice}
        />
      )}
    </S.Recognition>
  );
}

export { RecentRecognitionsExpandableTable };
